import { supabase } from '../../lib/supabase'
import type {
    DocumentFilters,
    DocumentType,
    ExpenseScope,
    FinancialDocument,
    Payment,
} from './types'

// ─── List Documents ────────────────────────────────────
export async function loadDocuments(
    organizationId: string,
    filters: DocumentFilters = {}
): Promise<{ rows: FinancialDocument[]; total: number }> {
    let query = supabase
        .from('financial_documents')
        .select('*,partners(name),work_items(name),cost_categories(name),document_lines(category_id,amount,description)', { count: 'exact' })
        .eq('organization_id', organizationId)
        .order('document_date', { ascending: false })

    if (filters.type) query = query.eq('document_type', filters.type)
    if (filters.approvalStatus) query = query.eq('approval_status', filters.approvalStatus)
    if (filters.postingStatus) query = query.eq('posting_status', filters.postingStatus)
    if (filters.paymentStatus) query = query.eq('payment_status', filters.paymentStatus)
    if (filters.workItemId) query = query.eq('work_item_id', filters.workItemId)
    if (filters.search) query = query.ilike('description', `%${filters.search}%`)

    const limit = filters.limit || 50
    const offset = filters.offset || 0
    query = query.range(offset, offset + limit - 1)

    const { data, error, count } = await query
    if (error) throw error

    const rows = (data || []).map((r: Record<string, unknown>) => ({
        ...r,
        partner_name: (r.partners as { name?: string } | null)?.name,
        work_item_name: (r.work_items as { name?: string } | null)?.name,
        category_name: (r.cost_categories as { name?: string } | null)?.name,
    })) as unknown as FinancialDocument[]

    return { rows, total: count || 0 }
}

// ─── Get Single Document ───────────────────────────────
export async function getDocument(id: string): Promise<FinancialDocument> {
    const { data, error } = await supabase
        .from('financial_documents')
        .select('*,partners(name),work_items(name),cost_categories(name),document_lines(category_id,amount,description)')
        .eq('id', id)
        .single()
    if (error) throw error
    return {
        ...data,
        partner_name: (data.partners as { name?: string } | null)?.name,
        work_item_name: (data.work_items as { name?: string } | null)?.name,
        category_name: (data.cost_categories as { name?: string } | null)?.name,
    } as unknown as FinancialDocument
}

// ─── Create Document ───────────────────────────────────
export interface CreateDocumentInput {
    organization_id: string
    document_number: string
    document_type: DocumentType
    description: string
    partner_id?: string
    work_item_id?: string
    department_id?: string
    location_id?: string
    cost_center_id?: string
    category_id?: string
    expense_scope?: ExpenseScope
    subtotal: number
    vat_amount: number
    pit_amount?: number
    pit_rate?: number
    document_date: string
    due_date?: string
    notes?: string
}

export async function createDocument(input: CreateDocumentInput) {
    const payload = {
        ...input,
        approval_status: 'draft' as const,
        posting_status: 'unposted' as const,
        payment_status: 'unpaid' as const,
        partner_id: input.partner_id || null,
        work_item_id: input.work_item_id || null,
        department_id: input.department_id || null,
        location_id: input.location_id || null,
        cost_center_id: input.cost_center_id || null,
        category_id: input.category_id || null,
        expense_scope: input.expense_scope || null,
        due_date: input.due_date || null,
        notes: input.notes || null,
    }
    const { data, error } = await supabase
        .from('financial_documents')
        .insert(payload)
        .select('id')
        .single()
    if (error) throw error
    return data
}

// ─── Update Document ───────────────────────────────────
export async function updateDocument(id: string, fields: Partial<CreateDocumentInput>) {
    const payload: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(fields)) {
        payload[key] = value === '' || value === undefined ? null : value
    }
    const { error } = await supabase.from('financial_documents').update(payload).eq('id', id)
    if (error) throw error
}

// ─── Lifecycle Actions ─────────────────────────────────
export async function submitDocument(id: string) {
    const { error } = await supabase
        .from('financial_documents')
        .update({ approval_status: 'submitted' })
        .eq('id', id)
    if (error) throw error
}

// Tìm phiếu duyệt đang chờ (nếu có) của chứng từ để đi qua đúng một luồng
// phê duyệt (bảng approvals + RPC decide_approval), giữ đồng bộ với hộp "Chờ duyệt".
async function pendingApprovalId(documentId: string): Promise<string | null> {
    const { data, error } = await supabase
        .from('approvals')
        .select('id')
        .eq('document_id', documentId)
        .eq('status', 'pending')
        .maybeSingle()
    if (error) throw error
    return data?.id ?? null
}

export async function approveDocument(id: string) {
    const approvalId = await pendingApprovalId(id)
    if (approvalId) {
        const { error } = await supabase.rpc('decide_approval', { target_id: approvalId, decision: 'approved' })
        if (error) throw error
        return
    }
    // Loại chứng từ không sinh phiếu duyệt (phải thu, phiếu thu/chi) → cập nhật trực tiếp
    const { error } = await supabase
        .from('financial_documents')
        .update({ approval_status: 'approved' })
        .eq('id', id)
    if (error) throw error
}

export async function rejectDocument(id: string) {
    const approvalId = await pendingApprovalId(id)
    if (approvalId) {
        const { error } = await supabase.rpc('decide_approval', { target_id: approvalId, decision: 'rejected' })
        if (error) throw error
        return
    }
    const { error } = await supabase
        .from('financial_documents')
        .update({ approval_status: 'rejected' })
        .eq('id', id)
    if (error) throw error
}

export async function postDocument(id: string) {
    const { error } = await supabase
        .from('financial_documents')
        .update({ posting_status: 'posted', posted_at: new Date().toISOString() })
        .eq('id', id)
    if (error) throw error
}

export async function reverseDocument(originalId: string, organizationId: string) {
    // Get the original document
    const original = await getDocument(originalId)

    // Create reversal document
    const { error: createError } = await supabase.from('financial_documents').insert({
        organization_id: organizationId,
        document_type: original.document_type,
        description: `Đảo: ${original.description}`,
        partner_id: original.partner_id,
        work_item_id: original.work_item_id,
        subtotal: -original.subtotal,
        vat_amount: -original.vat_amount,
        pit_amount: -(original.pit_amount || 0),
        pit_rate: original.pit_rate || 0,
        document_date: new Date().toISOString().slice(0, 10),
        approval_status: 'approved',
        posting_status: 'posted',
        posted_at: new Date().toISOString(),
        reversed_document_id: originalId,
    })
    if (createError) throw createError

    // Mark original as reversed
    const { error: updateError } = await supabase
        .from('financial_documents')
        .update({ posting_status: 'reversed' })
        .eq('id', originalId)
    if (updateError) throw updateError
}

// ─── Payments ──────────────────────────────────────────
export async function loadPayments(documentId: string): Promise<Payment[]> {
    const { data, error } = await supabase
        .from('payments')
        .select(`
            *,
            payment_allocations!inner(
                financial_document_id
            )
        `)
        .eq('payment_allocations.financial_document_id', documentId)
        .order('payment_date', { ascending: false })
    if (error) throw error
    return (data || []) as Payment[]
}

export interface RecordPaymentInput {
    organization_id: string
    document_id: string
    amount: number
    payment_date: string
    method?: string
    reference?: string
    bank_account?: string
    cash_account_id?: string
    note?: string
}

export async function recordPayment(input: RecordPaymentInput) {
    const { data, error } = await supabase.rpc('record_document_payment', {
        target_document: input.document_id,
        payment_amount: input.amount,
        payment_date: input.payment_date,
        payment_account: input.bank_account || null,
        payment_reference: input.reference || null,
        request_key: null,
        payment_cash_account: input.cash_account_id || null
    })
    if (error) throw error
    return data
}

// ─── Delete Document ───────────────────────────────────
export async function deleteDocument(id: string) {
    const { error } = await supabase.from('financial_documents').delete().eq('id', id)
    if (error) throw error
}
